import { useEffect, useState, type RefObject } from "react";

/**
 * Mirrors "hover to play" on touch devices, which have no hover state: the
 * tracked element is treated as "active" once it scrolls far enough into
 * the viewport, and inactive once it scrolls back out — matching common
 * mobile app behavior for inline video ads.
 *
 * No-ops (always returns false, no observer created) when `enabled` is
 * false, so it's safe to call unconditionally alongside mouse-hover state
 * and only switch over to it for touch/mobile previews.
 */
export const useAutoplayInView = (
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
  threshold = 0.6
): boolean => {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setInView(false);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref.current is read once to attach the observer; re-running on every ref change is unnecessary
  }, [enabled, threshold, ref]);

  return enabled && inView;
};
